// frontend/src/components/company/MissingDocsAlert.jsx
// Google Material Design inspired error alert for missing documents
import { useMemo } from "react";

/**
 * Display missing document requirements in a prominent, user-friendly format
 * @param {Array} errors - Array of missing doc errors from API
 * @param {Array} roles - Array of company roles (for UBO names)
 * @param {Function} onDismiss - Callback to dismiss alert
 * @param {Function} onUpload - Optional callback to navigate to documents tab
 */
export function MissingDocsAlert({ errors = [], roles = [], onDismiss, onUpload }) {
  // Group and format errors
  const { companyDocs, uboDocs } = useMemo(() => {
    const company = [];
    const ubo = [];

    errors.forEach((err) => {
      // Validate error structure
      if (!err || typeof err !== "object") return;

      if (err.type === "company") {
        company.push({
          kind: err.docKind || "unknown",
          reason: err.reason || "not_provided",
        });
      } else if (err.type === "ubo") {
        // Find UBO name from roles
        const uboIndex = typeof err.uboIndex === "number" ? err.uboIndex : -1;
        const uboRole = Array.isArray(roles) ? roles.filter((r) => r.role === "ubo").sort((a, b) => a.id - b.id)[uboIndex] : null;

        const uboName = uboRole ? `${uboRole.first_name || ""} ${uboRole.last_name || ""}`.trim() || `UBO #${uboIndex + 1}` : `UBO #${uboIndex + 1}`;

        ubo.push({
          uboIndex,
          uboName,
          kind: err.docKind || "unknown",
          reason: err.reason || "not_provided",
        });
      }
    });

    return { companyDocs: company, uboDocs: ubo };
  }, [errors, roles]);

  // Format reason text
  const formatReason = (reason) => {
    switch (reason) {
      case "not_provided":
        return "Not uploaded";
      case "expired":
        return "Expired";
      default:
        return reason;
    }
  };

  // Format document kind for display
  const formatDocKind = (kind) => {
    if (!kind) return "Unknown document";

    // Convert snake_case to Title Case
    return kind
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Don't render if no errors
  if (!errors || errors.length === 0) return null;

  return (
    <div
      className="alert alert-error"
      role="alert"
      aria-live="assertive"
      style={{
        backgroundColor: "#fef2f2",
        border: "1px solid #fca5a5",
        borderRadius: "8px",
        padding: "20px",
        marginBottom: "24px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
      }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
        {/* Error Icon */}
        <div
          style={{
            flexShrink: 0,
            width: "24px",
            height: "24px",
            borderRadius: "50%",
            backgroundColor: "#ef4444",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "16px",
            fontWeight: "bold",
          }}>
          !
        </div>

        <div style={{ flex: 1 }}>
          <h3
            style={{
              margin: "0 0 12px 0",
              fontSize: "16px",
              fontWeight: "600",
              color: "#991b1b",
            }}>
            Cannot activate company - Missing required documents
          </h3>

          {/* Company Documents */}
          {companyDocs.length > 0 && (
            <div style={{ marginBottom: companyDocs.length > 0 && uboDocs.length > 0 ? "16px" : "0" }}>
              <h4
                style={{
                  margin: "0 0 8px 0",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#7c2d12",
                }}>
                Company Documents
              </h4>
              <ul style={{ margin: 0, paddingLeft: "20px", color: "#7c2d12" }}>
                {companyDocs.map((doc, idx) => (
                  <li key={`company-${idx}`} style={{ marginBottom: "4px" }}>
                    <strong>{formatDocKind(doc.kind)}</strong> - {formatReason(doc.reason)}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* UBO Documents */}
          {uboDocs.length > 0 && (
            <div>
              <h4
                style={{
                  margin: "0 0 8px 0",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#7c2d12",
                }}>
                UBO Documents
              </h4>
              <ul style={{ margin: 0, paddingLeft: "20px", color: "#7c2d12" }}>
                {uboDocs.map((doc, idx) => (
                  <li key={`ubo-${idx}`} style={{ marginBottom: "4px" }}>
                    <strong>{doc.uboName}</strong> - {formatDocKind(doc.kind)} ({formatReason(doc.reason)})
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Action Buttons */}
          <div style={{ marginTop: "16px", display: "flex", gap: "8px", flexWrap: "wrap" }}>
            {onUpload && (
              <button
                type="button"
                onClick={onUpload}
                style={{
                  padding: "8px 16px",
                  fontSize: "14px",
                  fontWeight: "500",
                  borderRadius: "6px",
                  border: "1px solid #dc2626",
                  backgroundColor: "#dc2626",
                  color: "white",
                  cursor: "pointer",
                }}>
                Upload Documents
              </button>
            )}
            <button
              type="button"
              onClick={onDismiss}
              className="secondary"
              style={{
                padding: "8px 16px",
                fontSize: "14px",
                fontWeight: "500",
                borderRadius: "6px",
              }}>
              Dismiss
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
